import { useFieldArray } from "react-hook-form";
import {
  Box,
  Button,
  Card,
  Flex,
  Grid,
  GridItem,
  Text,
} from "@chakra-ui/react";
import { AppColors } from "@/styles/colors";
import { RequestStatusBadge } from "@/components/common/section-components/badge/RequestStatusBadge";
import { InputField } from "@/components/input-field/InputField";
import { SelectComponent } from "@/components/select/Select";
import { TextArea } from "@/components/text-area/TextArea";
import { Pen, Trash2, Trash2Icon } from "lucide-react";
import { handleTonChangeInInput } from "@/utils/query-string";
import { numbersWithCommaDecimal } from "@/utils/reg-expressions";
import { OrderStatus, SuppliesOrderStatus } from "@/constants/entity-status";

// La cantidad es decimal(10,3) en el BO Backend: 7 dígitos enteros + 3 decimales.
const MAX_CANTIDAD_INTEGER_DIGITS = 7;
const MAX_CANTIDAD_LENGTH = 11;

interface Props {
  control: any;
  errors: any;
  register: any;
  orderIndex: number;
  orderFieldsLength: number;
  isEditing: boolean;
  setEditingIndex: (index: number) => void;
  onDeleteOrder: (index: number) => void;
  onSaveAndCreateAnotherOrder: () => void;
  disableSaveAndCreateAnotherOrder?: boolean;
  productOptions: any[];
  dispatchPlaceOptions: any[];
  loadingProducts: boolean;
  loadingBranches: boolean;
  watchedOrders: any[];
  readOnly?: boolean;
  orderStatus?: string;
  hasAttempts?: boolean;
}

export const OrderCard = ({
  control,
  errors,
  register,
  orderIndex,
  orderFieldsLength,
  isEditing,
  setEditingIndex,
  onDeleteOrder,
  onSaveAndCreateAnotherOrder,
  disableSaveAndCreateAnotherOrder = false,
  productOptions,
  dispatchPlaceOptions,
  loadingProducts,
  loadingBranches,
  watchedOrders,
  readOnly = false,
  orderStatus,
  hasAttempts = false,
}: Props) => {
  const {
    fields: productFields,
    append: appendProduct,
    remove: removeProduct,
  } = useFieldArray({
    control,
    name: `orders.${orderIndex}.products`,
  });

  const current = watchedOrders?.[orderIndex];
  const totalLiters = (current?.products ?? []).reduce(
    (acc: number, p: any) => {
      const rawLiters = p?.tons;
      const parsedLiters =
        typeof rawLiters === "string"
          ? Number(rawLiters.replace(",", "."))
          : Number(rawLiters);

      return acc + (Number.isFinite(parsedLiters) ? parsedLiters : 0);
    },
    0,
  );

  const dispatchValue = current?.dispatchPlace;
  const normalizedDispatchValue = Array.isArray(dispatchValue)
    ? dispatchValue?.[0]
    : dispatchValue;
  const dispatchLabel = dispatchPlaceOptions?.find(
    (o) => String(o?.value ?? "") === String(normalizedDispatchValue ?? ""),
  )?.label;
  let dispatchDisplay = dispatchLabel || "";
  if (!dispatchDisplay) {
    if (normalizedDispatchValue) {
      dispatchDisplay = String(normalizedDispatchValue);
    } else {
      dispatchDisplay = "Sin despacho";
    }
  }

  const orderId = Number(current?.orderId ?? 0);
  const driverDisplay = current?.chofer ? `Chofer: ${current?.chofer}` : "Sin chofer";

  const isInactiveProductSelected = (productIndex: number) => {
    const raw = current?.products?.[productIndex]?.productId;
    const selected = Array.isArray(raw) ? raw?.[0] : raw;
    const selectedValue =
      selected !== undefined && selected !== null ? String(selected) : "";
    if (!selectedValue) return false;

    const opt = (productOptions ?? []).find(
      (o: any) => String(o?.value ?? "") === selectedValue,
    );
    return Boolean(opt?.isDisabled);
  };

  return (
    <Card.Root maxW="full" borderRadius="8px">
      <Card.Body
        paddingX="24px"
        paddingY="20px"
        borderRadius="8px"
        border={`1px solid ${AppColors.WHITE_BORDER_1}`}
      >
        <Flex flexDir="column" gap="16px">
          <Flex justifyContent="space-between" alignItems="center">
            <Flex gap="24px" alignItems="center" minH="32px">
              <Text fontWeight={700} fontSize="16px">
                {orderId ? `Pedido N°${orderId}` : "Pedido"}
              </Text>

              {!!totalLiters && (
                <>
                  <Box
                    width="1px"
                    height="20px"
                    background={AppColors.WHITE_BORDER_1}
                  />
                  <Text fontWeight={600} fontSize="14px">
                    {totalLiters} litros
                  </Text>
                </>
              )}

              {!!dispatchDisplay && (
                <>
                  <Box
                    width="1px"
                    height="20px"
                    background={AppColors.WHITE_BORDER_1}
                  />
                  <Text
                    fontWeight={600}
                    fontSize="14px"
                    color={AppColors.GRAY_TEXT_ALT}
                  >
                    {dispatchDisplay}
                  </Text>
                </>
              )}

              <>
                <Box
                  width="1px"
                  height="20px"
                  background={AppColors.WHITE_BORDER_1}
                />
                <Text
                  fontWeight={600}
                  fontSize="14px"
                  color={AppColors.GRAY_TEXT_ALT}
                >
                  {driverDisplay}
                </Text>
              </>
            </Flex>

            {orderStatus === SuppliesOrderStatus.PENDING && hasAttempts && (
              <Text
                fontWeight={600}
                fontSize="12px"
                color={AppColors.GRAY_TEXT_ALT}
              >
                En proceso de entrega
              </Text>
            )}

            <Flex gap="12px" alignItems="center">
              {!isEditing && !readOnly && (
                <Button
                  paddingX="12px"
                  height="32px"
                  onClick={() => setEditingIndex(orderIndex)}
                >
                  <Pen />
                  <Text fontWeight={600} fontSize="14px">
                    Editar
                  </Text>
                </Button>
              )}

              {readOnly && (
                <RequestStatusBadge status={orderStatus as OrderStatus} />
              )}

              {!readOnly && (
                <Trash2
                  height="32px"
                  aria-label="Eliminar pedido"
                  data-testid={`delete-order-${orderIndex}`}
                  onClick={() => {
                    if (orderFieldsLength > 1) {
                      onDeleteOrder(orderIndex);
                    }
                  }}
                  cursor={orderFieldsLength <= 1 ? "not-allowed" : "pointer"}
                />
              )}
            </Flex>
          </Flex>

          {isEditing && !readOnly && (
            <Flex flexDir="column" gap="16px">
              {productFields.map((product, productIndex) => (
                <Flex key={product.id} flexDir="column">
                  <Grid
                    templateColumns="260px 220px 44px"
                    columnGap="12px"
                    alignItems="end"
                  >
                    <GridItem paddingY="8px">
                      <SelectComponent
                        items={productOptions}
                        name={`orders.${orderIndex}.products.${productIndex}.productId`}
                        labelText="Producto"
                        placeholder="Seleccionar"
                        control={control}
                        error={
                          errors?.orders?.[orderIndex]?.products?.[productIndex]
                            ?.productId
                        }
                        loading={loadingProducts}
                        disabled={loadingProducts}
                        required
                        maxWidth="260px"
                      />
                    </GridItem>

                    <GridItem paddingY="8px">
                      <InputField
                        inputName={`orders.${orderIndex}.products.${productIndex}.tons`}
                        labelText="Cantidad de litros"
                        placeholder="Ingresar"
                        control={control}
                        type="text"
                        allowedPattern={numbersWithCommaDecimal}
                        onChange={(e) => handleTonChangeInInput(e, MAX_CANTIDAD_INTEGER_DIGITS)}
                        maxLength={MAX_CANTIDAD_LENGTH}
                        maxW="220px"
                      />
                    </GridItem>

                    <GridItem
                      paddingY="8px"
                      display="flex"
                      alignItems="flex-end"
                    >
                      <Trash2Icon
                        height="40px"
                        onClick={() => {
                          if (productFields.length > 1) {
                            removeProduct(productIndex);
                          }
                        }}
                        cursor={
                          productFields.length <= 1 ? "not-allowed" : "pointer"
                        }
                      />
                    </GridItem>
                  </Grid>

                  {isInactiveProductSelected(productIndex) && (
                    <Text
                      color={AppColors.ERROR_TEXT}
                      fontWeight={600}
                      fontSize="12px"
                      marginTop="2px"
                      maxW="260px"
                      fontStyle="italic"
                    >
                      Este producto está desactivado. Elije otro o elimina este
                      para continuar.
                    </Text>
                  )}
                </Flex>
              ))}

              <Grid
                templateColumns="260px 220px 44px"
                columnGap="12px"
                alignItems="end"
              >
                <GridItem paddingY="8px">
                  <Button
                    paddingX="12px"
                    height="32px"
                    width="100%"
                    onClick={() =>
                      appendProduct({
                        productId: "",
                        tons: "",
                      })
                    }
                  >
                    <Text fontWeight={600} fontSize="14px">
                      Agregar otro producto
                    </Text>
                  </Button>
                </GridItem>

                <GridItem paddingY="8px">
                  <Button
                    paddingX="12px"
                    height="32px"
                    width="100%"
                    onClick={onSaveAndCreateAnotherOrder}
                    bgColor={AppColors.GREEN_PRIMARY}
                    disabled={disableSaveAndCreateAnotherOrder}
                  >
                    <Text fontWeight={600} fontSize="14px">
                      Guardar y crear otro pedido
                    </Text>
                  </Button>
                </GridItem>

                <GridItem paddingY="8px" />
              </Grid>

              <Flex
                width="100%"
                height="1px"
                backgroundColor={AppColors.LIGHT_GRAY_TEXT}
                marginY="8px"
              />

              <Grid templateColumns="260px 1fr" columnGap="12px">
                <GridItem paddingX="16px" paddingY="8px">
                  <SelectComponent
                    items={dispatchPlaceOptions}
                    name={`orders.${orderIndex}.dispatchPlace`}
                    labelText="Lugar de despacho"
                    placeholder="Seleccionar"
                    control={control}
                    error={errors?.orders?.[orderIndex]?.dispatchPlace}
                    loading={loadingBranches}
                    disabled={loadingBranches}
                    required
                    fullWidth
                  />
                </GridItem>

                <GridItem paddingX="16px" paddingY="8px" width="100%">
                  <InputField
                    inputName={`orders.${orderIndex}.URLMaps`}
                    labelText="Destino (URL Google Maps)"
                    placeholder="Ingresar"
                    control={control}
                    type="text"
                    disabled={readOnly}
                    maxW="100%"
                  />
                </GridItem>

                <GridItem paddingX="16px" paddingY="8px" colSpan={2}>
                  <TextArea
                    register={register}
                    errors={errors}
                    inputName={`orders.${orderIndex}.observations`}
                    labelText="Observaciones"
                    placeholder="Ingresar"
                    helperText="Máximo 255 caracteres."
                  />
                </GridItem>
              </Grid>
            </Flex>
          )}
        </Flex>
      </Card.Body>
    </Card.Root>
  );
};
